import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneTilt, EnvelopeSimple, At, FacebookLogo, InstagramLogo, YoutubeLogo, LinkedinLogo } from "@phosphor-icons/react";
import { Reveal, SectionHeading } from "./Reveal";
import { SITE, SOCIALS } from "@/data/content";

const SOCIAL_ICONS = { FacebookLogo, InstagramLogo, YoutubeLogo, LinkedinLogo };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJECT_TYPES = ["Video Editing", "Reels", "Graphic Design", "AI Visuals", "Cinematic VFX", "Other"];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:border-gold/60 focus:ring-1 focus:ring-gold/40 transition-[border-color,box-shadow] duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", project_type: PROJECT_TYPES[0], message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.email_sent) {
        toast.success("Message sent — I'll get back to you soon.");
      } else {
        toast.success("Message received and saved. Email notification is briefly unavailable, but your enquiry is safely in my inbox.");
      }
      setForm({ name: "", email: "", project_type: PROJECT_TYPES[0], message: "" });
    } catch {
      // Static-hosting fallback (no backend): hand off to the visitor's email app
      const subject = encodeURIComponent(`New MARKMAXO enquiry: ${form.project_type}`);
      const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\nProject type: ${form.project_type}\n\n${form.message}`);
      window.location.href = `mailto:${SITE.email}?subject=${subject}&body=${body}`;
      toast.info("Opening your email app — just press send to deliver your enquiry.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-44 bg-surface/40 overflow-hidden grain">
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[700px] h-[400px] glow-gold blur-[130px] opacity-50 pointer-events-none" />
      <div className="max-w-[88rem] mx-auto px-6 md:px-10 relative">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-24">
          <div>
            <SectionHeading chapter="06" overline="Contact" title="LET'S CREATE SOMETHING" />
            <Reveal>
              <p className="text-base md:text-lg text-zinc-400 leading-relaxed max-w-md">
                Have a project, reel, graphic or creative idea in mind?
              </p>
              <p className="mt-4 font-display italic text-2xl md:text-3xl text-zinc-300 leading-snug">
                &ldquo;Let&apos;s turn your idea into something worth watching.&rdquo;
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-12 space-y-5">
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="contact-email-link"
                  className="group flex items-center gap-4 text-zinc-300 hover:text-gold transition-colors duration-300"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-gold">
                    <EnvelopeSimple size={20} weight="duotone" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-sm md:text-base font-medium">{SITE.email}</span>
                  </span>
                </a>
                <div className="flex items-center gap-4 text-zinc-500">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-gold">
                    <At size={20} weight="duotone" />
                  </span>
                  <div className="flex flex-wrap gap-3">
                    {SOCIALS.map((s) => {
                      const Icon = SOCIAL_ICONS[s.icon];
                      return (
                        <a
                          key={s.name}
                          href={s.url}
                          data-testid={`contact-social-${s.name.toLowerCase()}`}
                          aria-label={`${s.name} profile (placeholder link)`}
                          className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-zinc-300 hover:border-gold hover:text-gold transition-colors duration-300"
                        >
                          <Icon size={15} weight="fill" />
                          {s.name}
                        </a>
                      );
                    })}
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form onSubmit={submit} className="rounded-3xl border border-white/[0.08] bg-void/60 glass p-7 md:p-10 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Name</label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Email</label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-project-type" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Project Type</label>
                <select
                  id="contact-project-type"
                  data-testid="contact-project-type-select"
                  value={form.project_type}
                  onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                  className={`${inputCls} appearance-none cursor-pointer`}
                >
                  {PROJECT_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-void">{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Message</label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell me about your project…"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-[1.15rem] text-[15px] font-semibold uppercase tracking-wide text-void hover:bg-gold-soft disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? "Sending…" : "Send Message"}
                <PaperPlaneTilt size={17} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
